import React, { FC } from "react";
import moment, { Moment, MomentInput } from "moment";
import {
  Form,
  Button,
  DatePicker,
  Input as AntInput,
  Checkbox as AntCheckbox,
  Tooltip,
  Modal,
  Select as AntSelect,
} from "antd";
import { v4 as uuid } from "uuid";
import type { Rule } from "antd/lib/form";
import { displayFormat, toDisplayFormat } from "../../lib/utils/formatDate";
import { ReactComponent as CancelButtonIcon } from "../../img/buttons_icons/cancelButton_icon.svg";
import { ReactComponent as SubmitButtonIcon } from "../../img/buttons_icons/submitButton_icon.svg";
import { ReactComponent as AddVersionIcon } from "../../img/versions_icons/addVersion_icon.svg";
import { ReactComponent as EditIcon } from "../../img/versions_icons/editVersion_icon.svg";
import { ReactComponent as DeleteIcon } from "../../img/delete.svg";
import { ReactComponent as CloseVersionIcon } from "../../img/versions_icons/closeVersion_icon.svg";
import versionsTableUtils from "../../lib/utils/versionsTableUtils";
import { AnyObject } from "../../lib/interfaces/AnyObject";
import { TableRecord } from "../../lib/interfaces/TableRecord";

const buttonStyle = { display: "inline" };

// Period -------------------------------------------------------------------------------------------------
interface IPeriod {
  record: TableRecord;
  setFieldsValue: (prop: { fromDate: MomentInput }) => void;
  fromDateRequired?: boolean;
  toDateRequired?: boolean;
  allowedFromDate?: Moment;
  disabledFromDate?: (current: Moment) => boolean; // Функция для дизейбла дат в пикере
  disabledToDate?: (current: Moment) => boolean; // Функция для дизейбла дат в пикере
  toDateDisabled?: boolean; // Дизейбл поля ввода
  initialToDate?: Moment;
}

const Period = ({
  record,
  setFieldsValue,
  fromDateRequired = false,
  toDateRequired = false,
  allowedFromDate,
  disabledFromDate = (current) =>
    current.isSameOrBefore(record.fromDate, "day") || current.isAfter(record.toDate, "day"),
  disabledToDate = (current) => current.isBefore(record.fromDate, "day"),
  toDateDisabled = false,
  initialToDate,
}: IPeriod) => {
  const handleSetInitialValue = (field) => {
    setTimeout(() => {
      setFieldsValue(field);
    }, 0);
  };
  if (record.isCreated) {
    const nextFromDate = moment(record.fromDate).add(1, "day");
    const fromDate = allowedFromDate ? moment.max(nextFromDate, allowedFromDate) : nextFromDate;
    handleSetInitialValue({ fromDate });
    return (
      <>
        <Form.Item
          name="fromDate"
          className="form-item-required__hidden-error__mb0"
          rules={[{ required: fromDateRequired, message: "" }]}
          style={{ display: "inline-block", marginRight: "5px" }}
        >
          <DatePicker
            format={displayFormat}
            allowClear={false}
            disabledDate={disabledFromDate}
            placeholder=""
          />
        </Form.Item>
        <Form.Item
          style={{ display: "inline-block" }}
          className="form-item-required__hidden-error__mb0"
        >
          - {toDisplayFormat(record.toDate)}
        </Form.Item>
      </>
    );
  }
  if (record.isClosing) {
    handleSetInitialValue({ toDate: moment(initialToDate || record.toDate) });
    return (
      <>
        <Form.Item
          className="form-item-required__hidden-error__mb0"
          style={{ display: "inline-block", marginRight: "5px" }}
        >
          {toDisplayFormat(record.fromDate)} -
        </Form.Item>
        <Form.Item
          name="toDate"
          rules={[{ required: toDateRequired, message: "" }]}
          style={{ display: "inline-block" }}
          className="form-item-required__hidden-error__mb0"
        >
          <DatePicker
            format={displayFormat}
            allowClear={false}
            disabledDate={disabledToDate}
            disabled={toDateDisabled}
          />
        </Form.Item>
      </>
    );
  }
  if (record.fromDate && record.toDate) {
    return <>{`${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`}</>;
  }
  return <></>;
};

// Date -------------------------------------------------------------------------------------------------
interface IDate {
  dateName: string;
  record: TableRecord;
  setFieldsValue: (prop: { [key: string]: moment.Moment | string }) => void;
  required?: boolean;
  dateStringFormat?: string;
  allowClear?: boolean;
}

const Date = ({
  record,
  setFieldsValue,
  dateName,
  dateStringFormat,
  required,
  allowClear = false,
}: IDate) => {
  let fieldValue;
  if (record[dateName]) {
    if (dateStringFormat) {
      fieldValue = moment(record[dateName], dateStringFormat);
    } else {
      fieldValue = moment(record[dateName]);
    }
  } else {
    fieldValue = "";
  }

  const handleSetInitialValue = () => {
    setTimeout(() => {
      setFieldsValue({ [dateName]: fieldValue });
    }, 0);
  };
  if (record.isCreated || record.isEditing) {
    handleSetInitialValue();
    return (
      <>
        <Form.Item
          name={dateName}
          className="form-item-required__hidden-error__mb0"
          rules={[{ required, message: "" }]}
          style={{ display: "inline-block", marginRight: "5px" }}
        >
          <DatePicker placeholder="Выберите дату" format={displayFormat} allowClear={allowClear} />
        </Form.Item>
      </>
    );
  }
  return <>{record[dateName] ? record[dateName] : ""}</>;
};

// Input -------------------------------------------------------------------------------------------------
interface IInput {
  name: string;
  record: TableRecord;
  required?: boolean;
  rules?: Rule[];
  setFieldsValue: (prop: { [key: string]: string | number | undefined | undefined }) => void;
  allowClear?: boolean;
}

export const Input = ({
  record,
  setFieldsValue,
  name,
  required,
  rules = [],
  allowClear = false,
}: IInput) => {
  const fieldValue = record[name];
  const handleSetInitialValue = () => {
    setTimeout(() => {
      setFieldsValue({ [name]: fieldValue });
    }, 0);
  };
  if (record.isCreated || record.isEditing) {
    handleSetInitialValue();
    return (
      <Form.Item
        name={name}
        className="form-item-required__hidden-error__mb0"
        rules={[{ required, message: "" }, ...rules]}
      >
        <AntInput allowClear={allowClear} />
      </Form.Item>
    );
  }
  return fieldValue;
};
// Checkbox -------------------------------------------------------------------------------------------------
interface ICheckbox {
  name: string;
  record: TableRecord;
  required?: boolean;
  tooltipTitle?: string;
  setFieldsValue: (prop: { [key: string]: string | number }) => void;
}

export const Checkbox = ({ record, setFieldsValue, name, required, tooltipTitle }: ICheckbox) => {
  const checked = record[name];
  const handleSetInitialValue = () => {
    setTimeout(() => {
      setFieldsValue({ [name]: checked });
    }, 0);
  };
  if (record.isCreated || record.isEditing) {
    handleSetInitialValue();
    return (
      <Tooltip title={tooltipTitle}>
        <Form.Item
          name={name}
          className="form-item-required__hidden-error__mb0"
          rules={[{ required, message: "" }]}
          valuePropName="checked"
        >
          <AntCheckbox />
        </Form.Item>
      </Tooltip>
    );
  }
  return checked ? (
    <Tooltip title={tooltipTitle}>
      <AntCheckbox checked={checked} disabled />
    </Tooltip>
  ) : null;
};

// Select -------------------------------------------------------------------------------------------------
interface ISelect extends IInput {
  width?: string;
  dic: AnyObject[];
  presetAddVerionValue?: boolean; // сохранение значения при добавлении версии
  allowClear?: boolean;
}

export const Select = ({
  record,
  setFieldsValue,
  name,
  required,
  width = "250px",
  dic,
  presetAddVerionValue,
  allowClear = false,
}: ISelect) => {
  const fieldValue = record[name];
  const handleSetInitialValue = () => {
    if (record.isEditing || (record.isCreated && presetAddVerionValue)) {
      setTimeout(() => {
        setFieldsValue({ [name]: fieldValue });
      }, 0);
    } else {
      setTimeout(() => {
        setFieldsValue({ [name]: undefined });
      }, 0);
    }
  };
  if (record.isCreated || record.isEditing) {
    handleSetInitialValue();
    return (
      <Form.Item
        name={name}
        className="form-item-required__hidden-error__mb0"
        rules={[{ required, message: "" }]}
      >
        <AntSelect style={{ width }} allowClear={allowClear}>
          {dic.map((item) => (
            <AntSelect.Option key={item.id} value={item.id}>
              {item.name}
            </AntSelect.Option>
          ))}
        </AntSelect>
      </Form.Item>
    );
  }
  const displayValue = versionsTableUtils.geDicValueById(dic, fieldValue)?.name;
  return <>{displayValue}</>;
};

// SelectFilter -------------------------------------------------------------------------------------------------
interface ISelectFilter {
  name: string;
  record: TableRecord;
  required?: boolean;
  width?: string;
  dic: AnyObject[];
  setFieldsValue: (prop: { [key: string]: string | number }) => void;
  filterBy?: number;
  filterItem?: string;
  onChange?: (e: number) => void;
  disabled?: boolean;
}
export const SelectFilter = ({
  record,
  name,
  required,
  width = "250px",
  dic,
  filterBy,
  filterItem,
  disabled = false,
  onChange,
}: ISelectFilter) => {
  const fieldValue = record[name];
  if (record.isCreated || record.isEditing) {
    return (
      <Form.Item
        name={name}
        className="form-item-required__hidden-error__mb0"
        rules={[{ required, message: "" }]}
      >
        <AntSelect onChange={onChange} disabled={disabled} style={{ width }}>
          {filterBy && filterItem
            ? dic
                .filter((item) => item[filterItem] === filterBy)
                ?.map((item) => (
                  <AntSelect.Option key={item.id} value={item.id}>
                    {item.name}
                  </AntSelect.Option>
                ))
            : dic.map((item) => (
                <AntSelect.Option key={item.id} value={item.id}>
                  {item.name}
                </AntSelect.Option>
              ))}
        </AntSelect>
      </Form.Item>
    );
  }
  const displayValue = versionsTableUtils.geDicValueById(dic, fieldValue)?.name;
  return <>{displayValue}</>;
};

// AddButton -------------------------------------------------------------------------------------------------
interface IAddButton {
  record: TableRecord;
  hasAccess: boolean;
  isChangingForbidden: boolean;
  setChildTableData: (data: TableRecord[] | ((data: TableRecord[]) => TableRecord[])) => void;
  newSelectFieldValueToReset?: string;
}

const AddButton = ({
  record,
  hasAccess,
  isChangingForbidden,
  setChildTableData,
  newSelectFieldValueToReset,
}: IAddButton) => {
  const isButtonActive =
    hasAccess &&
    !record.isPastVersion &&
    !versionsTableUtils.getIsRecordChanging(record) &&
    !moment(record.fromDate).isSame(record.toDate, "day");

  if (isButtonActive) {
    return (
      <Tooltip title="Добавить версию" style={buttonStyle}>
        <Button
          className="button-icon"
          icon={<AddVersionIcon />}
          type="text"
          htmlType="button"
          disabled={isChangingForbidden}
          onClick={(e) => {
            e.stopPropagation();

            let newRecord;
            const obj = {
              ...record,
              key: uuid(),
              fromDate: moment(record.fromDate).add(1, "seconds"),
              isCreated: true,
              isCurrentVersion: false,
              isPastVersion: false,
              isLatestVersion: false,
            };

            if (newSelectFieldValueToReset) {
              newRecord = { ...obj, [`${newSelectFieldValueToReset}`]: "" };
            } else {
              newRecord = { ...obj };
            }

            setChildTableData((prevState) => {
              return [newRecord, ...prevState].sort(versionsTableUtils.sortVersionsByDate);
            });
          }}
        />
      </Tooltip>
    );
  }
  return <></>;
};

// EditButton -------------------------------------------------------------------------------------------------
interface IEditButton {
  record: TableRecord;
  hasAccess: boolean;
  isChangingForbidden: boolean;
  setChildTableData: (data: TableRecord[] | ((data: TableRecord[]) => TableRecord[])) => void;
  onClick?: () => void;
}

const EditButton = ({
  record,
  hasAccess,
  isChangingForbidden,
  setChildTableData,
  onClick = () => {},
}: IEditButton) => {
  const isButtonActive =
    hasAccess && !record.isPastVersion && !versionsTableUtils.getIsRecordChanging(record);

  if (isButtonActive) {
    return (
      <Tooltip title="Редактировать версию">
        <Button
          className="button-icon"
          icon={<EditIcon />}
          type="text"
          htmlType="button"
          disabled={isChangingForbidden}
          onClick={(e) => {
            e.stopPropagation();
            onClick();
            setChildTableData((prevState) =>
              prevState.map((item) => {
                if (item.key === record.key) return { ...item, isEditing: true };
                return item;
              })
            );
          }}
        />
      </Tooltip>
    );
  }
  return <></>;
};

// CloseButton -------------------------------------------------------------------------------------------------
interface ICloseButton {
  record: TableRecord;
  hasAccess: boolean;
  isChangingForbidden: boolean;
  setChildTableData: (data: TableRecord[] | ((data: TableRecord[]) => TableRecord[])) => void;
  errorOptions?: {
    value: boolean;
    title: string;
    content: string;
  };
  onClose?: () => void;
}

const CloseButton = ({
  record,
  hasAccess,
  isChangingForbidden,
  setChildTableData,
  errorOptions,
  onClose,
}: ICloseButton) => {
  const isButtonActive =
    hasAccess &&
    record.isLatestVersion &&
    !record.isPastVersion &&
    !versionsTableUtils.getIsRecordChanging(record);
  if (isButtonActive) {
    return (
      <Tooltip title="Изменить дату закрытия">
        <Button
          className="button-icon"
          icon={<CloseVersionIcon />}
          type="text"
          htmlType="button"
          disabled={isChangingForbidden}
          onClick={(e) => {
            e.stopPropagation();
            if (errorOptions?.value) {
              Modal.warning({
                title: errorOptions.title,
                content: errorOptions.content,
              });
            } else {
              setChildTableData((prevState) =>
                prevState.map((item) => {
                  if (item.key === record.key) {
                    const updatedRecord = {
                      ...item,
                      isClosing: true,
                    };
                    if (onClose) onClose();
                    return updatedRecord;
                  }
                  return item;
                })
              );
            }
          }}
        />
      </Tooltip>
    );
  }
  return <></>;
};

// SubmitButton -------------------------------------------------------------------------------------------------
interface ISubmitButton {
  record: TableRecord;
  submit: () => void;
  disabled?: {
    state: boolean;
    title: string;
  };
}

const SubmitButton = ({ record, submit, disabled }: ISubmitButton) => {
  if (versionsTableUtils.getIsRecordChanging(record))
    return (
      <Tooltip title={disabled?.state ? disabled?.title : "Подтвердить"}>
        <Button
          className="button-icon"
          icon={<SubmitButtonIcon />}
          type="text"
          htmlType="button"
          onClick={(event) => {
            event.stopPropagation();
            submit();
          }}
          disabled={disabled?.state}
        />
      </Tooltip>
    );
  return <></>;
};

// CancelButton -------------------------------------------------------------------------------------------------
interface ICancelButton {
  record: TableRecord;
  setChildTableData: (data: TableRecord[] | ((data: TableRecord[]) => TableRecord[])) => void;
  resetFields: () => void;
}

const CancelButton = ({ record, resetFields, setChildTableData }: ICancelButton) => {
  if (versionsTableUtils.getIsRecordChanging(record))
    return (
      <Tooltip title="Отменить">
        <Button
          className="button-icon"
          icon={<CancelButtonIcon />}
          type="text"
          htmlType="button"
          onClick={(e) => {
            e.stopPropagation();
            if (record.isCreated) {
              setChildTableData((prevState) => prevState.filter((obj) => !obj.isCreated));
            } else {
              setChildTableData((prevState) =>
                prevState.map((item) => ({
                  ...item,
                  isEditing: false,
                  isClosing: false,
                  isChangingProlongation: false,
                }))
              );
            }
            resetFields();
          }}
        />
      </Tooltip>
    );
  return <></>;
};

// DeleteButton ---------------------------------//----------------------------------------------------------------
interface IDeleteButton {
  record: TableRecord;
  onClick: () => void;
  title: string;
}

const DeleteButton = ({ onClick, title }: IDeleteButton) => {
  return (
    <Tooltip title={title}>
      <Button
        className="button-icon"
        icon={<DeleteIcon />}
        type="text"
        htmlType="button"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
      />
    </Tooltip>
  );
};

const ChangeVersionsTable = {
  Period,
  Input,
  AddButton,
  EditButton,
  CloseButton,
  SubmitButton,
  CancelButton,
  DeleteButton,
  Select,
  SelectFilter,
  Date,
  Checkbox,
};

export default ChangeVersionsTable;
